/**
 * API layer for XCEL prelicensing (backend app `xcel_prelicensing`, mounted at
 * `/api/xcel-prelicensing/`). Every read is local on the server; nothing here
 * reaches XCEL directly — refresh and runs are queued and polled.
 */

import type {
  AccountPrograms,
  AccountProgress,
  Decision,
  DecisionResult,
  HierarchyOption,
  MappingCounts,
  MappingFilters,
  MappingPage,
  QueueableRunType,
  QueuedRun,
  RefreshResult,
  RunActivity,
  SettingsPatch,
  XcelAccess,
  XcelErrorBody,
  XcelRun,
  XcelSettings,
  XcelStatus,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const BASE = '/api/xcel-prelicensing';

/** Non-2xx response. `message` is the backend's user-safe `detail`; `code` is stable. */
export class XcelApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'XcelApiError';
    this.status = status;
    this.code = code;
  }
}

function headers(): HeadersInit {
  const token = localStorage.getItem('wb.authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Token ${token}` } : {}),
  };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${BASE}${path}`, { ...init, headers: headers() });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as Partial<XcelErrorBody> | null;
    throw new XcelApiError(
      body?.detail || 'XCEL prelicensing is temporarily unavailable. Please try again.',
      response.status,
      body?.code || 'unavailable',
    );
  }
  return (await response.json()) as T;
}

function post<T>(path: string, body: unknown = {}): Promise<T> {
  return request(path, { method: 'POST', body: JSON.stringify(body) });
}

export function fetchXcelAccess(signal?: AbortSignal): Promise<XcelAccess> {
  return request('/my-access/', { signal });
}

export function fetchSettings(signal?: AbortSignal): Promise<XcelSettings> {
  return request('/settings/', { signal });
}

export function patchSettings(patch: SettingsPatch): Promise<XcelSettings> {
  return request('/settings/', { method: 'PATCH', body: JSON.stringify(patch) });
}

export function fetchStatus(signal?: AbortSignal): Promise<XcelStatus> {
  return request('/status/', { signal });
}

export function queueProbe(): Promise<QueuedRun> {
  return post('/probe/');
}

export function queueRun(runType: QueueableRunType): Promise<QueuedRun> {
  return post('/runs/', { run_type: runType });
}

export function fetchRuns(signal?: AbortSignal): Promise<{ results: XcelRun[] }> {
  return request('/runs/?limit=20', { signal });
}

export function fetchRun(runId: number, signal?: AbortSignal): Promise<XcelRun> {
  return request(`/runs/${runId}/`, { signal });
}

export function fetchRunActivity(runId: number, signal?: AbortSignal): Promise<RunActivity> {
  return request(`/runs/${runId}/activity/?limit=200`, { signal });
}

function mappingQuery(filters: MappingFilters, cursor?: string | null): string {
  const query = new URLSearchParams();
  if (filters.state !== 'all') query.set('state', filters.state);
  if (filters.evidence !== 'all') query.set('evidence', filters.evidence);
  if (filters.search.trim()) query.set('search', filters.search.trim());
  if (filters.leader) query.set('leader_id', String(filters.leader.id));
  if (filters.md) query.set('md_id', String(filters.md.id));
  if (filters.smd) query.set('smd_id', String(filters.smd.id));
  if (cursor) query.set('cursor', cursor);
  const text = query.toString();
  return text ? `?${text}` : '';
}

export function fetchMappings(filters: MappingFilters, cursor: string | null, signal?: AbortSignal): Promise<MappingPage> {
  return request(`/mappings/${mappingQuery(filters, cursor)}`, { signal });
}

export function fetchMappingCounts(filters: MappingFilters, signal?: AbortSignal): Promise<MappingCounts> {
  return request(`/mappings/counts/${mappingQuery(filters)}`, { signal });
}

export function fetchHierarchyOptions(
  role: 'leader' | 'md' | 'smd',
  search: string,
  signal?: AbortSignal,
): Promise<{ results: HierarchyOption[] }> {
  const query = new URLSearchParams({ role, search });
  return request(`/mappings/hierarchy-options/?${query}`, { signal });
}

export function postDecision(studentId: number, decision: Decision): Promise<DecisionResult> {
  return post(`/mappings/${studentId}/decision/`, decision);
}

export function fetchAccountProgress(accountId: number, signal?: AbortSignal): Promise<AccountProgress> {
  return request(`/accounts/${accountId}/progress/`, { signal });
}

export function fetchAccountPrograms(accountId: number, offset: number, signal?: AbortSignal): Promise<AccountPrograms> {
  return request(`/accounts/${accountId}/programs/?offset=${offset}&limit=20`, { signal });
}

export function requestAccountRefresh(accountId: number, force = false): Promise<RefreshResult> {
  return post(`/accounts/${accountId}/refresh/`, { force });
}
